import { useQuery } from '@tanstack/react-query';

import { apiFetch } from '@/lib/api/client';
import { queryKeys } from '@/lib/queryKeys';

/**
 * 떠 있는 오브가 **혼자 하는 말** — 관리자가 적어 둔 세 줄을 대신한다 (사용자 지시 2026-09-18).
 *
 * ## 고정 목록이 있던 자리다
 *
 * 오브는 설정 화면에 적힌 세 줄(「도와드릴까요?」·「찾는 화면이 있으면 말씀해 주세요.」)을 돌려
 * 말했다. 회사가 무엇을 적든 그 말은 **오늘 이 사람과 무관**했고, 세 시간마다 돌아오는 같은 문장은
 * 대화가 아니라 벽지다. 「진짜 AI 니까 상황에 맞는 말을 했으면 좋겠다」가 사용자의 말이었다.
 *
 * ## 판단은 이미 서버에 있었다
 *
 * 홈 인사말이 쓰던 신호 엔진을 그대로 쓴다(`HomeGreetingService.orbLines`) — 회의·할일·결재함·
 * 날씨·구내식당·생일·연차를 **이미 읽고 있던** 그 엔진이다. 새로 판단할 것이 없어서 붙였고, 그래서
 * 「30분 뒤에 회의가 있어요」와 「결재를 기다리는 문서가 3건 있어요」가 그 자리에서 나온다.
 *
 * ## 한 줄이 아니라 한 **묶음**을 받는다
 *
 * 홈은 화면을 열 때 한 번 한 줄을 받지만 오브는 몇 분에 한 번씩 말한다. 말할 때마다 물으면 200명이
 * 종일 켜 둔 화면에서 결재함·할일·회의 조회가 쉬지 않고 돈다 — 조회 횟수가 **말하는 횟수가 아니라
 * 화면을 켜 둔 시간**에 비례하도록 묶음으로 받고, 고르는 일은 화면이 한다.
 */

/**
 * 말 한 줄.
 *
 * @property signal 이 말을 끌어낸 신호({@code GreetingSignal} 의 이름). 방금 한 말과 **같은 화제**를
 *                  연달아 고르지 않으려고 쓴다 — 글자만 보고는 「남은 회의가 2건 있어요」와 「회의는
 *                  잘 하셨나요」가 같은 화제인 줄 알 수 없다
 */
export type OrbLine = { signal: string; text: string };

/**
 * 서버가 주는 한 묶음.
 *
 * @property enabled 이 사람에게 말을 걸어도 되는가. 홈 인사말을 끈 사람은 `false` 다
 *                   (사용자 지시 2026-09-18). **빈 목록과 갈라 둔 까닭**은 아래 {@link useOrbLines}
 *                   에 있다 — 「할 말이 없다」와 「말하지 말라」가 같은 모양이면, 끈 사람의 화면에서
 *                   오히려 기본 문장이 돌아온다
 */
type OrbLines = { enabled: boolean; lines: OrbLine[] };

/**
 * 서버가 아무것도 주지 못했을 때의 말.
 *
 * **오브는 입을 닫지 않는다.** 한때 「목록이 비면 말하지 않는다」로 뒀다가, 아무도 끈 적 없는데
 * 오브가 영영 조용해진 적이 있다(2026-09-17). 이제 그 침묵은 서버가 죽었을 때도 오지 않는다 —
 * 신호를 못 받으면 여기로 물러선다.
 *
 * `signal` 이 전부 `FALLBACK` 인 것은 일부러다. 화제를 가를 수 없는 줄들이라 서로 「같은 화제」로
 * 묶이고, 그래서 연달아 나오지 않는다.
 */
export const ORB_LINES_FALLBACK: OrbLine[] = [
  { signal: 'FALLBACK', text: '도와드릴까요?' },
  { signal: 'FALLBACK', text: '찾는 화면이 있으면 말씀해 주세요.' },
  { signal: 'FALLBACK', text: '이번 주 일정, 정리해 드릴까요?' },
];

/**
 * 다시 물어 오는 간격.
 *
 * 오브가 말하는 간격(기본 3분)보다 **길다**. 한 묶음으로 서너 번 말하고 나서 새 묶음을 받는 셈이라,
 * 「두 시간 안에 회의」나 「오전 9~12시 식단」 같은 창을 놓치지 않으면서도 조회는 열 배로 줄어든다.
 */
const REFRESH_MS = 10 * 60 * 1000;

/**
 * 지금 할 수 있는 말들. **`null` 이면 이번 차례는 말하지 않는다.**
 *
 * 네 가지 경우를 하나의 값으로 줄인다 — 고르는 쪽이 매번 이 판단을 되풀이하지 않도록.
 *
 * | 상태 | 값 | 왜 |
 * | --- | --- | --- |
 * | 아직 안 왔다 | `null` | 스위치가 꺼져 있는지 **아직 모른다**. 모르는 채로 말하면 끈 사람이 첫 마디를 듣는다 |
 * | 사람이 껐다 | `null` | 「AI 가 말 거는 게 싫은 사람은 어디서든 싫다」(사용자 지시 2026-09-18) |
 * | 못 받았다 | 기본 문장 | 서버가 죽었다고 오브가 입을 닫지는 않는다(2026-09-17 의 침묵 사고) |
 * | 받았는데 비었다 | 기본 문장 | 전사 손잡이가 내려갔거나 신호를 하나도 못 모은 것이다 |
 *
 * 가운데 둘이 이 훅의 전부다. **「말하지 말라」와 「할 말이 없다」를 가르지 못하면** 끈 사람의
 * 화면에서 오히려 고정 문장 셋이 돌아온다 — 없애려고 지운 바로 그 목록이.
 */
export function useOrbLines(): OrbLine[] | null {
  const query = useQuery({
    queryKey: queryKeys.orbLines,
    queryFn: () => apiFetch<OrbLines>('/v1/home/greeting/lines'),
    staleTime: REFRESH_MS,
    /*
      **스스로 다시 묻는다.** 오브는 레이아웃에 붙어 있어 화면을 옮겨도 이 훅이 다시 서지 않는다 —
      `staleTime` 만 두면 아침에 받은 묶음을 퇴근할 때까지 들고 있게 되고, 오후 다섯 시에
      「오늘 구내식당에 …」를 말한다.
    */
    refetchInterval: REFRESH_MS,
    /*
      실패를 물고 늘어지지 않는다. 이것은 **혼잣말**이라 못 받으면 기본 문장으로 말하면 그만이고,
      재시도가 쌓이면 화면마다 떠 있는 오브가 그만큼 요청을 늘린다.
    */
    retry: false,
  });

  // 아직 모른다. 스위치가 꺼져 있을 수도 있으니 이번 차례는 건너뛴다 — 다음 차례는 몇 분 뒤다.
  if (query.isPending) return null;
  // 못 받았다(오류·오프라인). 기본 문장으로 말한다 — 여기서 조용해지면 서버 사정이 침묵이 된다.
  if (!query.data) return ORB_LINES_FALLBACK;
  // 사람이 껐다. 기본 문장으로도 물러서지 않는다.
  if (!query.data.enabled) return null;

  return query.data.lines.length > 0 ? query.data.lines : ORB_LINES_FALLBACK;
}

/**
 * 이번에 할 말을 고른다.
 *
 * **방금 한 화제는 뺀다.** 빼서 아무것도 안 남으면 빼지 않는다 — 겹치지 않으려다 입을 다무는 것은
 * 본말이 바뀐 것이다(서버의 `pick` 도 같은 규칙으로 최근 화제를 다룬다).
 *
 * @param lines      {@link useOrbLines} 가 준 값. `null` 이면 **말하지 않는다**
 * @param lastSignal 방금 한 말의 신호. 없으면 `null`
 */
export function pickOrbLine(lines: OrbLine[] | null | undefined, lastSignal: string | null): OrbLine | null {
  if (!lines || lines.length === 0) return null;

  const fresh = lastSignal ? lines.filter((line) => line.signal !== lastSignal) : lines;
  const from = fresh.length > 0 ? fresh : lines;
  return from[Math.floor(Math.random() * from.length)] ?? null;
}
